import React, { useState, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { OnyxMascotFace } from "../shared/OnyxMascot";
import "./styles.css";

// Blog post data fetched from server API
interface BlogPostData {
  slug: string;
  title: string;
  metaDescription: string;
  h1: string;
  content: string;
  category: string;
  lang: string;
  publishDate: string;
  readTime: string;
}

function BlogIndex({ posts, onSelect }: { posts: BlogPostData[]; onSelect: (slug: string) => void }) {
  return (
    <div>
      <div className="text-center mb-16">
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
          Onyx AI <span className="text-[#c0a0e0]">Blog</span>
        </h1>
        <p className="text-lg text-[#8a7a9a] max-w-2xl mx-auto">
          Tips, guides, and insights to help Malaysian businesses automate customer support with AI.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {posts.map((post) => (
          <article
            key={post.slug}
            className="rounded-xl border border-[#2a2040] p-6 hover:border-[#a078d0]/50 transition-all cursor-pointer"
            style={{ background: "linear-gradient(145deg, #14121e, #1a1530)" }}
            onClick={() => onSelect(post.slug)}
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs px-2 py-1 rounded-full bg-[#a078d0]/10 text-[#c0a0e0] border border-[#7b5ea7]/30">
                {post.category}
              </span>
              <span className="text-xs text-[#5a4a7a]">{post.readTime}</span>
            </div>
            <h2 className="text-lg font-semibold text-white mb-2 line-clamp-2">{post.h1}</h2>
            <p className="text-sm text-[#8a7a9a] line-clamp-3">{post.metaDescription}</p>
            <div className="mt-4 text-sm text-[#a078d0] hover:text-[#c0a0e0] transition-colors">
              Read more →
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function BlogPost({ post }: { post: BlogPostData }) {
  // Update document meta for SEO
  useEffect(() => {
    document.title = post.title;

    const setMeta = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', "content", post.metaDescription);
    setMeta('link[rel="canonical"]', "href", `https://onyxai.chat/blog/${post.slug}`);
    setMeta('meta[property="og:title"]', "content", post.title);
    setMeta('meta[property="og:description"]', "content", post.metaDescription);
    setMeta('meta[property="og:url"]', "content", `https://onyxai.chat/blog/${post.slug}`);
    setMeta('meta[property="og:type"]', "content", "article");

    // article:published_time — create tag if not present in HTML shell
    let publishedMeta = document.querySelector('meta[property="article:published_time"]');
    if (!publishedMeta) {
      publishedMeta = document.createElement("meta");
      publishedMeta.setAttribute("property", "article:published_time");
      document.head.appendChild(publishedMeta);
    }
    publishedMeta.setAttribute("content", post.publishDate);
  }, [post]);

  return (
    <article className="max-w-3xl mx-auto">
      <a href="/blog" className="text-sm text-[#a078d0] hover:text-[#c0a0e0] mb-6 inline-block">
        ← Back to Blog
      </a>
      <div className="flex items-center gap-3 mb-4">
        <span className="text-xs px-2 py-1 rounded-full bg-[#a078d0]/10 text-[#c0a0e0] border border-[#7b5ea7]/30">
          {post.category}
        </span>
        <span className="text-xs text-[#5a4a7a]">{post.publishDate}</span>
        <span className="text-xs text-[#5a4a7a]">{post.readTime}</span>
      </div>
      <h1 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">{post.h1}</h1>
      <div className="blog-content" dangerouslySetInnerHTML={{ __html: post.content }} />

      {/* CTA section */}
      <div className="mt-12 p-8 rounded-xl border border-[#a078d0]/30 text-center" style={{ background: "linear-gradient(145deg, #1a1028, #201540)" }}>
        <h3 className="text-xl font-bold text-white mb-3">Ready to automate your customer support?</h3>
        <p className="text-[#8a7a9a] mb-6">Start free with Onyx AI — no credit card required.</p>
        <a href="/portal" className="inline-block px-8 py-3 bg-[#a078d0] hover:bg-[#8060b0] text-white font-semibold rounded-lg transition-colors">
          Get Started Free
        </a>
      </div>
    </article>
  );
}

function App() {
  const [posts, setPosts] = useState<BlogPostData[]>([]);
  const [currentPost, setCurrentPost] = useState<BlogPostData | null>(null);
  const [loading, setLoading] = useState(true);

  // Extract slug from URL path
  const getSlugFromPath = () => {
    const path = window.location.pathname;
    const match = path.match(/^\/blog\/(.+)/);
    return match ? match[1] : null;
  };

  // Fetch a single post with full content
  const fetchPost = (slug: string) => {
    setLoading(true);
    fetch(`/api/blog/posts/${slug}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.post) setCurrentPost(data.post);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    // Fetch all blog posts index (no content, just metadata)
    fetch("/api/blog/posts")
      .then((r) => r.json())
      .then((data) => {
        setPosts(data.posts || []);
        setLoading(false);

        // If URL has a slug, fetch that full post
        const slug = getSlugFromPath();
        if (slug) fetchPost(slug);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleSelect = (slug: string) => {
    window.history.pushState({}, "", `/blog/${slug}`);
    fetchPost(slug);
  };

  // Handle browser back/forward
  useEffect(() => {
    const handlePop = () => {
      const slug = getSlugFromPath();
      if (slug) {
        fetchPost(slug);
      } else {
        setCurrentPost(null);
      }
    };
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "#0a0a12" }}>
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b border-[#2a2040]/50" style={{ background: "rgba(10,10,18,0.85)" }}>
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <OnyxMascotFace size={28} />
            <span className="text-lg font-semibold text-white" style={{ fontFamily: "Orbitron, sans-serif" }}>
              ONYX <span className="text-[#c0a0e0] font-normal text-sm">AI</span>
            </span>
          </a>
          <nav className="flex items-center gap-6">
            <a href="/" className="text-sm text-[#8a7a9a] hover:text-[#c0a0e0] transition-colors">Home</a>
            <a href="/blog" className="text-sm text-[#c0a0e0]">Blog</a>
            <a href="/docs" className="text-sm text-[#8a7a9a] hover:text-[#c0a0e0] transition-colors">Docs</a>
            <a href="/portal" className="text-sm px-4 py-2 bg-[#a078d0] hover:bg-[#8060b0] text-white rounded-lg transition-colors">
              Get Started
            </a>
          </nav>
        </div>
      </header>

      {/* Content */}
      <main className="pt-28 pb-20 px-6 max-w-6xl mx-auto">
        {loading ? (
          <div className="text-center text-[#8a7a9a]">Loading...</div>
        ) : currentPost ? (
          <BlogPost post={currentPost} />
        ) : (
          <BlogIndex posts={posts} onSelect={handleSelect} />
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-[#2a2040] py-8 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <p className="text-sm text-[#5a4a7a]">
            &copy; {new Date().getFullYear()} Cloudforge Studio. All rights reserved.
          </p>
          <div className="flex items-center gap-2">
            <OnyxMascotFace size={20} />
            <span className="text-xs text-[#5a4a7a]">Onyx AI by Cloudforge Studio</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

const root = createRoot(document.getElementById("root")!);
root.render(<App />);
